import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { getDoctorChatHistory, uploadDoctorFile, deleteDoctorChatMessage } from '../../services/doctorService';
import { getSlotStatus } from '../../services/patientService';
import { useCall } from '../../context/CallContext';
import { useSlotTimer } from '../../hooks/useSlotTimer';
import { formatCountdown } from '../../utils/helpers';

// ── Message Bubble ─────────────────────────────────────────────────────────

function Bubble({ msg, isMe, onDeleteForEveryone }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const holdTimer = useRef(null);
  const menuRef   = useRef(null);
  const t = new Date(msg.timestamp || msg.createdAt)
    .toLocaleTimeString('en', { hour:'2-digit', minute:'2-digit', hour12:true });
  const msgAge = Date.now() - new Date(msg.timestamp || msg.createdAt).getTime();

  useEffect(() => {
    if (!menuOpen) return;
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', h);
    document.addEventListener('touchstart', h);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('touchstart', h); };
  }, [menuOpen]);

  const startHold = () => { holdTimer.current = setTimeout(() => setMenuOpen(true), 500); };
  const cancelHold = () => clearTimeout(holdTimer.current);

  if (msg.deletedForEveryone) {
    return (
      <div className={`flex mb-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
        <div className="flex items-center gap-1.5 px-3 py-2 rounded-2xl border border-border bg-white/60">
          <i className="fa-solid fa-ban text-text-muted text-xs" />
          <p className="text-xs text-text-muted italic">This message was deleted</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex mb-2 ${isMe ? 'justify-end' : 'justify-start'} relative`}>
      <div className={`max-w-[78%] rounded-2xl px-3 py-2 select-none ${isMe ? 'bg-green-600 text-white rounded-br-sm' : 'bg-white text-text-primary rounded-bl-sm shadow-sm border border-border'}`}
        onTouchStart={isMe ? startHold : undefined}
        onTouchEnd={isMe ? cancelHold : undefined}
        onTouchMove={isMe ? cancelHold : undefined}
        onContextMenu={isMe ? (e) => { e.preventDefault(); setMenuOpen(true); } : undefined}
      >
        {msg.type === 'text'  && <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.message}</p>}
        {msg.type === 'image' && <img src={msg.fileUrl} alt="img" className="rounded-xl max-w-full max-h-56 object-cover cursor-pointer" onClick={() => window.open(msg.fileUrl,'_blank')} />}
        {msg.type === 'pdf'   && <a href={msg.fileUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2"><i className="fa-solid fa-file-pdf text-danger text-xl" /><span className="text-sm underline">{msg.fileName||'Document.pdf'}</span></a>}
        {msg.type === 'voice' && <audio controls src={msg.fileUrl} className="h-8 max-w-full" />}
        <p className={`text-[9px] mt-1 text-right ${isMe ? 'text-white/70' : 'text-text-muted'}`}>
          {t}{isMe && <i className={`fa-solid fa-check-double ml-1 ${msg.isRead ? 'text-blue-300':'opacity-50'}`} />}
        </p>
      </div>
      {isMe && menuOpen && (
        <div ref={menuRef} className="absolute z-50 bottom-full mb-1 right-0 bg-white rounded-2xl shadow-xl border border-border overflow-hidden min-w-[180px]">
          <button onClick={() => setMenuOpen(false)}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-text-secondary hover:bg-surface">
            <i className="fa-solid fa-xmark" /> Cancel
          </button>
        </div>
      )}
    </div>
  );
}

function DateSep({ date }) {
  return (
    <div className="flex items-center gap-2 my-3">
      <div className="flex-1 h-px bg-border" />
      <span className="text-[10px] text-text-muted px-2">
        {new Date(date).toLocaleDateString('en', { weekday:'long', day:'numeric', month:'short' })}
      </span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

// Call UI now lives entirely in <GlobalCallOverlay/>, shared globally via
// CallContext — removed the duplicate IncomingCallUI/ActiveCallUI components.

// ── Main DoctorChatPage ────────────────────────────────────────────────────
export default function DoctorChatPage() {
  const { conversationId } = useParams();
  const navigate           = useNavigate();
  const location           = useLocation();
  const { user, role }     = useAuth();
  const { socket }         = useSocket();

  // FIX: Staff chat karte waqt doctorId use karo (patient ne conversation doctorId se banayi hai)
  // Staff ka apna _id staffId hota hai jo conversationId mein nahi hota → remoteUserId empty milta tha
  const myId = (role === 'staff' && user?.doctorId)
    ? user.doctorId.toString()
    : (user?._id?.toString() || '');

  // Safe remoteUserId: find 24-char ObjectId that is not myId
  // Wait for myId to be available before extracting
  const remoteUserId = (() => {
    if (!conversationId || !myId) return '';
    return conversationId.split('_').find(id => id.length === 24 && id !== myId) || '';
  })();

  // appointmentId passed via navigation state (from DoctorAppointments or DoctorChatList)
  const appointmentId = location.state?.appointmentId || null;

  const [messages, setMessages]     = useState([]);
  const [text, setText]             = useState('');
  const [theyTyping, setTheyTyping] = useState(false);
  const [loading, setLoading]       = useState(true);
  const [uploading, setUploading]   = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [slotStatus, setSlotStatus] = useState(null);

  const bottomRef   = useRef(null);
  const typingTimer = useRef(null);
  const fileRef     = useRef(null);

  // Patient name — backend getSlotStatus now returns top-level patientName
  // (populated from Appointment.patientId.name), with location.state as a
  // fallback for the case where the chat was opened before that fix existed
  // in cached/old appointment records.
  const patientName = slotStatus?.patientName
    || slotStatus?.appointmentData?.patientId?.name
    || location.state?.patientName
    || 'Patient';

  // WebRTC — single SHARED global call engine (same instance GlobalCallOverlay
  // uses). startCall now needs the targetId passed explicitly per call.
  const webrtc = useCall();

  // Slot timer (countdown to slot end)
  // Use actual slot duration from backend (not hardcoded 10)
  const slotDuration = slotStatus?.duration || 10;
  const slotTimer = useSlotTimer(
    slotStatus?.slotStart ? new Date(slotStatus.slotStart).toTimeString().slice(0,5) : null,
    slotStatus?.slotStart ? new Date(slotStatus.slotStart).toISOString().split('T')[0] : null,
    slotDuration
  );

  // Check slot status (for video consults)
  const checkSlotStatus = useCallback(async () => {
    if (!appointmentId) return;
    try {
      const res = await getSlotStatus(appointmentId);
      setSlotStatus(res);
      const nowExpired = res.status === 'expired';
      setIsReadOnly(nowExpired);
      // Auto-end active call when slot expires
      if (nowExpired && webrtc.callState !== 'idle') {
        webrtc.endCall();
        import('react-hot-toast').then(m =>
          m.default.error('Slot expired — consultation ended automatically')
        );
      }
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId, webrtc.callState]);

  // Load history
  // NOTE: page-ownership for incoming calls is handled automatically by
  // useCallRTC via window.__RTC_PAGE_OWNERS (registers remoteId on mount).

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDoctorChatHistory(conversationId, { limit: 50 });
      setMessages(res.messages || []);
    } catch { toast.error('Failed to load chat'); }
    finally { setLoading(false); }
  }, [conversationId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Check slot status on mount + poll every 15s (shorter for accurate expiry detection)
  useEffect(() => {
    checkSlotStatus();
    const interval = setInterval(checkSlotStatus, 15000);
    return () => clearInterval(interval);
  }, [checkSlotStatus]);

  // Watch slotTimer — when it hits 'expired', lock immediately + end call
  useEffect(() => {
    if (slotTimer.status === 'expired') {
      setIsReadOnly(true);
      if (webrtc.callState !== 'idle') {
        webrtc.endCall();
        import('react-hot-toast').then(m =>
          m.default.error('Slot time ended — call ended automatically')
        );
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotTimer.status]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:'smooth' }); }, [messages]);

  // Socket
  useEffect(() => {
    if (!socket) return;
    const onMsg = (msg) => {
      if (msg.conversationId === conversationId || msg.from === remoteUserId) {
        setMessages(p => [...p, { ...msg, isRead: false }]);
        socket.emit('mark-read', { conversationId });
      }
    };
    const onTyping   = ({ from }) => { if (from === remoteUserId) setTheyTyping(true); };
    const onStopType = ({ from }) => { if (from === remoteUserId) setTheyTyping(false); };
    const onRead     = () => setMessages(p => p.map(m => ({ ...m, isRead: true })));

    socket.on('receive-message',     onMsg);
    socket.on('user-typing',         onTyping);
    socket.on('user-stopped-typing', onStopType);
    socket.on('messages-read',       onRead);
    socket.emit('mark-read', { conversationId });

    return () => {
      window.__chatPageHandlingCall = null;
      socket.off('receive-message',     onMsg);
      socket.off('user-typing',         onTyping);
      socket.off('user-stopped-typing', onStopType);
      socket.off('messages-read',       onRead);
    };
  }, [socket, conversationId, remoteUserId]);

  const handleDeleteForEveryone = useCallback(async (messageId) => {
    try {
      await deleteDoctorChatMessage(messageId);
      setMessages(prev => prev.map(m =>
        m._id === messageId ? { ...m, deletedForEveryone: true, message: '', fileUrl: null } : m
      ));
    } catch (err) {
      const expired = err.response?.data?.expired;
      toast.error(expired ? '5-minute window has passed' : 'Could not delete message');
    }
  }, []);

  const sendMessage = () => {
    if (!text.trim() || !socket || isReadOnly) return;
    const msg = { _id: Date.now().toString(), conversationId, from: myId, senderId: myId, message: text, type:'text', timestamp: new Date().toISOString(), isRead: false };
    setMessages(p => [...p, msg]);
    socket.emit('send-message', { to: remoteUserId, message: text, type: 'text', senderRole: 'doctor' });
    setText('');
    setShowAttach(false);
  };

  const handleTextChange = (val) => {
    setText(val);
    if (socket) {
      socket.emit('typing', { to: remoteUserId });
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => socket.emit('stop-typing', { to: remoteUserId }), 1500);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file || isReadOnly) return;
    setUploading(true); setShowAttach(false);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('receiverId', remoteUserId);
      const res = await uploadDoctorFile(fd);
      setMessages(p => [...p, { ...res.message, from: myId, timestamp: new Date().toISOString() }]);
    } catch (err) { toast.error('Upload failed: ' + err.message); }
    finally { setUploading(false); }
  };

  const grouped = messages.reduce((acc, msg) => {
    const d = new Date(msg.timestamp || msg.createdAt).toDateString();
    if (!acc[d]) acc[d] = [];
    acc[d].push(msg);
    return acc;
  }, {});

  // Is this a video consult chat (time-locked)?
  const isVideoConsult = !!appointmentId;
  const chatDisabled   = isReadOnly;

  return (
    <div className="flex flex-col bg-white w-full" style={{height:"100dvh",maxWidth:"768px",margin:"0 auto",borderLeft:"1px solid #E2E8F0",borderRight:"1px solid #E2E8F0"}}>

      {/* Call UI rendered globally by <GlobalCallOverlay/> using the same
          shared webrtc instance — no duplicate call screen here anymore. */}

      {/* Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-border flex-shrink-0">
        <div className="flex items-center gap-3 px-4 h-14">
          <button onClick={() => navigate(-1)} className="btn-icon">
            <i className="fa-solid fa-arrow-left text-text-primary" />
          </button>
          <div className="w-9 h-9 rounded-full bg-primary-light flex items-center justify-center flex-shrink-0 relative">
            <i className="fa-solid fa-user text-primary text-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-text-primary">{patientName}</p>
            <p className="text-xs">
              {theyTyping
                ? <span className="text-success">typing...</span>
                : <span className="text-text-muted">{isVideoConsult ? 'Video Consult' : 'Chat'}</span>}
            </p>
          </div>
          {/* Call buttons — only when slot is active */}
          {!chatDisabled && (
            <div className="flex gap-2">
              <button onClick={() => webrtc.startCall('audio', user?.name || 'Doctor', remoteUserId, false, patientName)} disabled={webrtc.callState !== 'idle'}
                className="w-9 h-9 bg-green-100 rounded-full flex items-center justify-center active:scale-95 disabled:opacity-40">
                <i className="fa-solid fa-phone text-green-600 text-sm" />
              </button>
              <button onClick={() => webrtc.startCall('video', user?.name || 'Doctor', remoteUserId, false, patientName)} disabled={webrtc.callState !== 'idle'}
                className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center active:scale-95 disabled:opacity-40">
                <i className="fa-solid fa-video text-blue-600 text-sm" />
              </button>
            </div>
          )}
        </div>

        {/* Slot timer banner */}
        {isVideoConsult && slotStatus?.status === 'active' && slotTimer && (
          <div className="bg-primary px-4 py-1.5 flex items-center gap-2">
            <i className="fa-solid fa-clock text-white/80 text-xs" />
            <p className="text-[11px] text-white font-medium">
              Slot ends in <span className="font-black">{formatCountdown(slotTimer)}</span>
            </p>
          </div>
        )}

        {/* Slot expired banner */}
        {isVideoConsult && isReadOnly && (
          <div className="bg-red-50 border-b border-red-100 px-4 py-2 flex items-center gap-2">
            <i className="fa-solid fa-lock text-danger text-xs" />
            <p className="text-[11px] text-danger font-semibold">Slot expired — chat is now read-only</p>
          </div>
        )}

        {/* Active slot banner */}
        {isVideoConsult && slotStatus?.status === 'active' && !isReadOnly && (
          <div className="bg-green-50 border-b border-green-100 px-4 py-1.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-success animate-pulse flex-shrink-0" />
            <p className="text-[10px] text-green-700 font-semibold">Video Consultation Active</p>
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 px-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-8 h-8 border-2 border-primary-light border-t-primary rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-8">
            <div className="w-16 h-16 bg-primary-light rounded-full flex items-center justify-center">
              <i className="fa-solid fa-comment-medical text-primary text-2xl" />
            </div>
            <p className="font-semibold text-text-primary">No messages yet</p>
            <p className="text-xs text-text-muted">Patient messages will appear here during the slot</p>
          </div>
        ) : Object.entries(grouped).map(([date, msgs]) => (
          <div key={date}>
            <DateSep date={date} />
            {msgs.map((msg, i) => (
              <Bubble key={msg._id || i} msg={msg}
                isMe={msg.from === myId || msg.senderId === myId}
                onDeleteForEveryone={handleDeleteForEveryone} />
            ))}
          </div>
        ))}
        {theyTyping && (
          <div className="flex gap-1 items-center px-3 py-2 bg-white rounded-2xl rounded-bl-sm w-16 shadow-sm border border-border mb-2">
            {[0,1,2].map(i => <span key={i} className="w-1.5 h-1.5 bg-text-muted rounded-full animate-bounce" style={{ animationDelay:`${i*0.15}s` }} />)}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Attach menu */}
      {showAttach && !chatDisabled && (
        <>
          <div className="backdrop" onClick={() => setShowAttach(false)} />
          <div className="fixed bottom-20 left-4 right-4 z-40 bg-white rounded-2xl shadow-xl border border-border p-4">
            <div className="grid grid-cols-3 gap-3">
              {[
                { icon:'fa-image',    label:'Photo',    accept:'image/*',  color:'bg-blue-100 text-info' },
                { icon:'fa-file-pdf', label:'Document', accept:'.pdf',      color:'bg-red-100 text-danger' },
                { icon:'fa-microphone',label:'Audio',   accept:'audio/*',  color:'bg-green-100 text-success' },
              ].map(item => (
                <button key={item.label} onClick={() => { fileRef.current.accept = item.accept; fileRef.current.click(); }}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-surface">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${item.color}`}>
                    <i className={`fa-solid ${item.icon} text-lg`} />
                  </div>
                  <span className="text-xs text-text-secondary">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
      <input ref={fileRef} type="file" className="hidden"
        onChange={e => { handleFileUpload(e.target.files[0]); e.target.value = ''; }} />

      {/* Input bar */}
      {chatDisabled ? (
        <div className="bg-red-50 border-t border-red-100 px-4 py-3 flex items-center justify-center gap-2">
          <i className="fa-solid fa-lock text-danger text-sm" />
          <p className="text-sm text-danger font-medium">Slot expired — chat locked</p>
        </div>
      ) : (
        <div className="bg-white border-t border-border px-3 py-2 safe-bottom">
          <div className="flex items-end gap-2">
            <button onClick={() => setShowAttach(!showAttach)} className="btn-icon flex-shrink-0 mb-0.5">
              {uploading
                ? <i className="fa-solid fa-spinner animate-spin text-primary" />
                : <i className="fa-solid fa-paperclip text-text-secondary text-lg" />}
            </button>
            <div className="flex-1 bg-surface border border-border rounded-2xl px-4 py-2.5 min-h-[42px] flex items-center">
              <textarea rows={1} value={text} onChange={e => handleTextChange(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder="Reply to patient..."
                className="flex-1 bg-transparent resize-none outline-none text-sm text-text-primary placeholder:text-text-muted max-h-28"
                style={{ lineHeight:'1.4' }} />
            </div>
            <button onClick={sendMessage} disabled={!text.trim()}
              className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${text.trim() ? 'bg-primary' : 'bg-gray-100'}`}>
              <i className={`fa-solid fa-paper-plane text-sm ${text.trim() ? 'text-white' : 'text-text-muted'}`} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
